import { createStore } from 'vuex'
import SFetch from '@app/utils/sfetch'

import * as mutationTypes from './mutation-types'

import api from '../api/index'
import GraphPeriodsInterpreter from '../utils/graph-periods-interpreter'
import periodsRange from '../utils/periods-range'

const interpreter = new GraphPeriodsInterpreter()

const LINECHART_GRAPH_TYPE = 'linechart'
const PIECHART_GRAPH_TYPE = 'piechart'

const translateGraphValues = (graphValues, reportTags) => {
  return graphValues.map((graphValue, index) => {
    const formulaType = reportTags.includes(graphValue.formula)
      ? 'reportTag'
      : 'formula'

    return {
      key: index,
      id: graphValue.id,
      name: graphValue.name, // is Object like {en: 'series name in english', nl: 'waarschijnlijk'}
      position: graphValue.position,
      budget: graphValue.budget,
      budgetSetId: graphValue.budgetSetId,
      seriesType: graphValue.seriesType,
      valueType: graphValue.valueType,
      delta: graphValue.delta,
      relative: graphValue.relative,
      formulaType: formulaType,
      reportTag: formulaType === 'reportTag' ? graphValue.formula : null,
      formula: formulaType === 'formula' ? graphValue.formula : null,
    }
  })
}

export const actions = {
  setCurrentLocale({ commit }, { locale }) {
    commit(mutationTypes.SET_CURRENT_LOCALE, locale)
  },
  deleteGraph({ state }) {
    api.destroy(state.graphEndpoints.destroyUrl)
  },
  setCurrentTitle({ commit }, { title }) {
    commit(mutationTypes.SET_CURRENT_TITLE, title)
  },
  setDescription({ commit }, { description }) {
    commit(mutationTypes.SET_DESCRIPTION, description)
  },
  setGraphType({ commit }, { graphType }) {
    commit(mutationTypes.SET_GRAPH_TYPE, graphType)
  },
  setGraphErrors({ commit }, { errors }) {
    commit(mutationTypes.SET_GRAPH_ERRORS, errors)
  },
  setGraphValues({ commit }, { graphValues }) {
    commit(mutationTypes.SET_GRAPH_VALUES, graphValues)
  },
  setMaximumValues({ commit }, { maximum }) {
    commit(mutationTypes.SET_MAXIMUM_VALUES, { maximum })
  },
  setMinimumValues({ commit }, { minimum }) {
    commit(mutationTypes.SET_MINIMUM_VALUES, { minimum })
  },
  setGraphValueName({ commit }, { position, name }) {
    commit(mutationTypes.SET_GRAPH_VALUE_NAME, { position, name })
  },
  setGraphValueAttributes({ commit }, { position, attributes }) {
    commit(mutationTypes.SET_GRAPH_VALUE_ATTRIBUTES, { position, attributes })
  },
  setFrequency({ commit }, { frequency }) {
    commit(mutationTypes.SET_FREQUENCY, { frequency })
  },
  setPriorYears({ commit }, { priorYears }) {
    commit(mutationTypes.SET_PRIOR_YEARS, { priorYears })
  },
  setYearType({ commit }, { yearType }) {
    commit(mutationTypes.SET_YEAR_TYPE, { yearType })
  },
  saveGraph({ state, dispatch, getters, commit }) {
    commit(mutationTypes.UPDATE_SAVING_STATE, true)
    api
      .save(
        state.graphEndpoints.submitUrl,
        state.graphEndpoints.submitMethod,
        getters.formattedReportGraphForSubmit
      )
      .then((data) => {
        dispatch('setGraphErrors', { errors: {} })
        commit(
          mutationTypes.SET_GRAPH_VALUES,
          translateGraphValues(data.values, state.reportTemplate.reportTags)
        )
        commit(mutationTypes.SET_OLD_GRAPH_PERIODS, data.periods)
        commit(
          mutationTypes.UPDATE_PREVIEW_DATA,
          JSON.parse(data.renderData || null)
        )
        commit(mutationTypes.SET_GRAPH_ENDPOINTS, data.endpoints)
        commit(mutationTypes.UPDATE_SAVING_STATE, false)
      })
      .catch((error) => {
        if (error.status === 422) {
          dispatch('setGraphErrors', { errors: error.body.errors })
        } else if (error.status !== 0) {
          alert('Something went wrong')
        }
        commit(mutationTypes.UPDATE_SAVING_STATE, false)
      })
  },
  setXAxisValue({ commit }, { range }) {
    commit(mutationTypes.SET_XAXIS_RANGE, range)
  },
}

export default function createGraphEditorStore(props) {
  return createStore({
    state() {
      // This is the main store for the graph editor, for all of its components.
      // Considering it's a fairly complex component, some planned structure
      // is needed to make it maintainable.
      //
      // Settings are divided in section and each section has a short comment
      // explaining its goal.
      return {
        // This section is for settings that affect how the editor works but
        // don't affect the graph itself. They're left flat because sub components
        // usually need just one.
        readOnly: props.read_only,
        canEditDescription: props.can_edit_description,
        currentLocale: props.language,
        canSelectBudget: props.can_select_budget,
        saving: false, // To track when we're running the save call
        // Backend endpoints related to this graph. Components should not be using this directly.
        graphEndpoints: {
          destroyUrl: props.report_graph_endpoints.destroy_url,
          submitUrl: props.report_graph_endpoints.submit_url,
          submitMethod: props.report_graph_endpoints.submit_method,
        },
        // Properties that belong directly to the graph object.
        graph: {
          id: props.report_graph.id,
          title: props.report_graph.title, // is Object like {en: 'title in english', nl: 'gedachten'}
          availableLocales: props.report_graph.available_locales,
          graphType: props.report_graph.graph_type,
          description: props.report_graph.description,
          yearType: props.report_graph.year_type,
          maximum: props.report_graph.maximum,
          minimum: props.report_graph.minimum,
        },
        // Validation errors will be populated here
        errors: {},
        // Properties defining the periods (a.k.a. the x-axis)
        oldGraphPeriods: props.graph_periods,
        graphPeriods: {
          ...interpreter.stringsToOptions(
            props.graph_periods,
            props.ledgers_per_year
          ),
          range: periodsRange(props.graph_periods),
          ledgersPerYear: props.ledgers_per_year,
        },
        // Properties defining the value series
        graphValues: translateGraphValues(
          SFetch.camelizeJSON(props.graph_values),
          props.report_template.report_tags
        ),
        // Properties related to the report this graph belongs to
        reportTemplate: {
          id: props.report_template.id,
          name: props.report_template.name,
          reportTags: props.report_template.report_tags,
          editUrl: props.report_template.edit_url,
          graphsUrl: props.report_template.graphs_url,
        },
        // Budget Set properties for budget selection
        budgetSets: props.budget_sets.map((budgetSet) => {
          return {
            id: budgetSet.id,
            name: budgetSet.name,
          }
        }),
        // Dates data for time label next to series name
        datesData: {
          periodFormat: props.dates_data.period_format,
          currentLedgerEndDate: props.dates_data.current_ledger_end_date,
          graphPeriodsRangesBookingYear: props.dates_data.graph_periods_ranges
            ? props.dates_data.graph_periods_ranges.booking_year
            : null,
          graphPeriodsRangesCalendarYear: props.dates_data.graph_periods_ranges
            ? props.dates_data.graph_periods_ranges.calendar_year
            : null,
        },
        // Data relevant for the preview component
        preview: {
          enabled: props.preview.enabled,
          renderData: JSON.parse(props.preview.render_data || null),
        },
      }
    },
    getters: {
      graphCurrentTitle: (state) => {
        return state.graph.title[state.currentLocale]
      },
      graphAvailableLanguages: (state) => {
        return state.graph.availableLocales.map((locale) => {
          return {
            locale: locale,
            language: I18n.t('js.languages')[locale],
          }
        })
      },
      isPersisted: (state) => {
        return !!state.graph.id
      },
      isGraphTypeLinechart: (state) => {
        return state.graph.graphType === LINECHART_GRAPH_TYPE
      },
      hasSpecificDates: (state) => {
        return !!state.datesData.currentLedgerEndDate
      },
      graphValuesErrors: (state) => {
        return state.errors.reportGraphValues || []
      },
      notEnoughValuesForTimeSeriesOffset: (state) => {
        return state.graphValues.filter((value) => !value._destroy).length < 2
      },
      graphErrors: (state) => {
        return state.errors
      },
      budgetOptions: (state) => {
        if (state.canSelectBudget) {
          const options = [
            {
              value: false,
              name: I18n.t(
                'js.report_graph.edit.graph_values.budget_option_labels.actuals'
              ),
            },
          ]
          state.budgetSets.forEach((budgetSet) =>
            options.push({ value: budgetSet.id, name: budgetSet.name })
          )
          return options
        } else {
          return [
            {
              value: false,
              name: I18n.t(
                'js.report_graph.edit.graph_values.budget_option_labels.actuals'
              ),
            },
            {
              value: true,
              name: I18n.t(
                'js.report_graph.edit.graph_values.budget_option_labels.budget'
              ),
            },
          ]
        }
      },
      formattedReportGraphForSubmit: (state) => {
        const graphPeriodsAttributes =
          state.graph.graphType === PIECHART_GRAPH_TYPE
            ? []
            : interpreter.optionsToString({
                ...state.graphPeriods,
                previousPeriods: state.oldGraphPeriods,
              })

        const graphParams = {
          reportGraph: {
            graphType: state.graph.graphType,
            description: state.graph.description,
            maximum: state.graph.maximum,
            minimum: state.graph.minimum,
            yearType: state.graph.yearType,
            reportGraphPeriodsAttributes: graphPeriodsAttributes,
            reportGraphValuesAttributes: state.graphValues.map((value) => {
              const graphValue = {
                id: value.id,
                _destroy: value._destroy,
                position: value.position,
                formula:
                  value.formulaType === 'reportTag'
                    ? value.reportTag
                    : value.formula,
                budget: value.budget,
                budgetSetId: value.budgetSetId,
                seriesType: value.seriesType,
                valueType: value.valueType,
                delta: value.delta,
                relative: value.relative,
              }

              state.graph.availableLocales.forEach(
                (locale) => (graphValue[`name_${locale}`] = value.name[locale])
              )

              return graphValue
            }),
          },
        }

        state.graph.availableLocales.forEach(
          (locale) =>
            (graphParams.reportGraph[`title_${locale}`] =
              state.graph.title[locale])
        )

        return graphParams
      },
    },
    mutations: {
      [mutationTypes.SET_CURRENT_LOCALE](state, locale) {
        state.currentLocale = locale
      },
      [mutationTypes.SET_CURRENT_TITLE](state, title) {
        state.graph.title[state.currentLocale] = title
      },
      [mutationTypes.SET_DESCRIPTION](state, description) {
        state.graph.description = description
      },
      [mutationTypes.SET_GRAPH_TYPE](state, graphType) {
        state.graph.graphType = graphType
        state.preview.renderData = null
      },
      [mutationTypes.SET_GRAPH_VALUES](state, graphValues) {
        state.graphValues = graphValues
        state.preview.renderData = null
      },
      [mutationTypes.SET_MAXIMUM_VALUES](state, { maximum }) {
        state.graph.maximum = maximum
        state.preview.renderData = null
      },
      [mutationTypes.SET_MINIMUM_VALUES](state, { minimum }) {
        state.graph.minimum = minimum
        state.preview.renderData = null
      },
      [mutationTypes.SET_GRAPH_VALUE_NAME](state, { name, position }) {
        const graphValue = state.graphValues.find(
          (value) => value.position === position
        )
        if (graphValue) {
          graphValue.name[state.currentLocale] = name
          state.graphValues.splice(state.graphValues.indexOf(graphValue), 1, {
            ...graphValue,
          })
          state.preview.renderData = null
        }
      },
      [mutationTypes.SET_GRAPH_VALUE_ATTRIBUTES](
        state,
        { attributes, position }
      ) {
        const graphValue = state.graphValues.find(
          (value) => value.position === position
        )
        if (graphValue) {
          state.graphValues.splice(state.graphValues.indexOf(graphValue), 1, {
            ...graphValue,
            ...attributes,
          })
          if (
            Object.keys(attributes).length !== 1 ||
            !Object.keys(attributes).includes('collapsed')
          ) {
            state.preview.renderData = null
          }
        }
      },
      [mutationTypes.SET_FREQUENCY](state, { frequency }) {
        state.graphPeriods.frequency = frequency
        state.preview.renderData = null
      },
      [mutationTypes.SET_PRIOR_YEARS](state, { priorYears }) {
        state.graphPeriods.priorYears = priorYears
        state.preview.renderData = null
      },
      [mutationTypes.SET_YEAR_TYPE](state, { yearType }) {
        state.graph.yearType = yearType
        state.preview.renderData = null
      },
      [mutationTypes.SET_GRAPH_ERRORS](state, errors) {
        state.errors = errors
      },
      [mutationTypes.SET_XAXIS_RANGE](state, range) {
        state.graphPeriods.range = range
        state.preview.renderData = null
      },
      [mutationTypes.SET_OLD_GRAPH_PERIODS](state, periods) {
        state.oldGraphPeriods = periods
      },
      [mutationTypes.SET_GRAPH_ENDPOINTS](state, endpoints) {
        state.graphEndpoints = endpoints
      },
      [mutationTypes.UPDATE_PREVIEW_DATA](state, renderData) {
        state.preview.renderData = renderData
      },
      [mutationTypes.UPDATE_SAVING_STATE](state, saving) {
        state.saving = saving
      },
    },
    actions,
  })
}
